"use client";

import { BrandMark } from "@/components/brand";
import { ReactNode } from "react";
import { QuizProgressBar } from "./QuizProgressBar";

interface QuizLayoutProps {
  children: ReactNode;
  onBack?: () => void;
  onClose?: () => void;
  showProgress?: boolean;
  showBack?: boolean;
  className?: string;
}

export function QuizLayout({
  children,
  onBack,
  onClose,
  showProgress = true,
  showBack = true,
  className = "",
}: QuizLayoutProps) {
  return (
    <div className="min-h-[100dvh] bg-[#0F0F14] text-white">
      {showProgress && <QuizProgressBar onBack={onBack} onClose={onClose} showBack={showBack} />}
      {!showProgress && (
        <header className="mx-auto flex h-16 max-w-5xl items-center px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <BrandMark size={36} className="h-9 w-9 object-contain" alt="" priority />
            <span className="font-bold tracking-tight text-white">ChickyTutor</span>
          </div>
        </header>
      )}
      <main className={`mx-auto max-w-2xl px-4 pb-12 pt-6 sm:px-6 sm:pt-10 ${className}`}>
        {children}
      </main>
    </div>
  );
}

interface QuizQuestionContainerProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  badge?: string;
  animate?: boolean;
}

export function QuizQuestionContainer({
  title,
  subtitle,
  children,
  badge,
  animate = true,
}: QuizQuestionContainerProps) {
  return (
    <div className={`space-y-7 ${animate ? "motion-safe:animate-fade-in" : ""}`}>
      <div className="space-y-3 text-center">
        {badge && (
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#FD954F]">
            {badge}
          </span>
        )}
        <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mx-auto max-w-xl text-base leading-relaxed text-[#AEAEB2] sm:text-lg">
            {subtitle}
          </p>
        )}
      </div>

      <div>{children}</div>
    </div>
  );
}

interface ContinueButtonProps {
  onClick: () => void;
  disabled?: boolean;
  children?: ReactNode;
  variant?: "primary" | "secondary";
  className?: string;
}

export function ContinueButton({
  onClick,
  disabled = false,
  children = "Continue",
  variant = "primary",
  className = "",
}: ContinueButtonProps) {
  if (variant === "secondary") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`
          min-h-12 w-full rounded-xl px-6 py-3 text-base font-bold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F0F14]
          ${
            disabled
              ? "cursor-not-allowed bg-[#1B1C22] text-gray-600"
              : "border border-white/15 bg-[#15161B] text-gray-200 hover:border-[#FD954F]/60 hover:text-white"
          }
          ${className}
        `}
      >
        {children}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        min-h-[52px] w-full rounded-xl px-6 py-3 text-base font-bold transition-colors duration-200 active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F0F14]
        ${
          disabled
            ? "cursor-not-allowed bg-[#1B1C22] text-gray-600"
            : "bg-[#FD954F] text-[#1C1C1E] hover:bg-[#F28747]"
        }
        ${className}
      `}
    >
      {children}
    </button>
  );
}

// Keyboard hint component
export function KeyboardHint() {
  return (
    <p className="mt-6 hidden text-center text-sm text-gray-500 sm:block">
      Use{" "}
      <kbd className="rounded border border-white/10 bg-[#15161B] px-1.5 py-0.5 font-mono text-xs text-gray-400">
        A
      </kbd>{" "}
      to{" "}
      <kbd className="rounded border border-white/10 bg-[#15161B] px-1.5 py-0.5 font-mono text-xs text-gray-400">
        F
      </kbd>{" "}
      to choose
    </p>
  );
}
